
import { supabase } from "@/integrations/supabase/client";

export const usePropertyValidation = () => {
  const validateRequired = (value: string, fieldName: string): string | null => {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    return null;
  };

  const checkDuplicateProperty = async (
    address: string, 
    city: string, 
    state: string, 
    zipCode: string
  ): Promise<{ isDuplicate: boolean; existingProperty?: any }> => {
    if (!address.trim() || !city.trim() || !state.trim() || !zipCode.trim()) {
      return { isDuplicate: false };
    }

    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, address, city, state, zip_code, seller_id, price')
        .eq('address', address.trim())
        .eq('city', city.trim())
        .eq('state', state.trim())
        .eq('zip_code', zipCode.trim())
        .eq('status', 'active')
        .limit(1);

      if (error) {
        console.error('Error checking for duplicate property:', error);
        return { isDuplicate: false };
      }

      return {
        isDuplicate: data && data.length > 0,
        existingProperty: data && data.length > 0 ? data[0] : undefined
      };
    } catch (error) {
      console.error('Error checking for duplicate property:', error);
      return { isDuplicate: false };
    }
  };

  const validateNumber = (value: string, fieldName: string, min?: number, max?: number): string | null => {
    if (!value) return null;
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return `${fieldName} must be a valid number`;
    }
    
    if (min !== undefined && numValue < min) {
      return `${fieldName} must be at least ${min}`;
    }
    
    if (max !== undefined && numValue > max) {
      return `${fieldName} must be at most ${max}`;
    }
    
    return null;
  };

  const validateEmail = (email: string): string | null => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  };

  const validateZipCode = (zipCode: string): string | null => {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipCode) return 'ZIP code is required';
    if (!zipRegex.test(zipCode)) return 'Please enter a valid ZIP code';
    return null;
  };

  const validateImages = (images: File[]): string | null => {
    if (!images || images.length === 0) {
      return 'At least one property image is required';
    }
    return null;
  };

  const validatePropertyData = (data: any, images?: File[]) => {
    const errors: Record<string, string> = {};

    // Required field validation
    const requiredValidation = validateRequired(data.address, 'Address');
    if (requiredValidation) errors.address = requiredValidation;

    const cityValidation = validateRequired(data.city, 'City');
    if (cityValidation) errors.city = cityValidation;

    const stateValidation = validateRequired(data.state, 'State');
    if (stateValidation) errors.state = stateValidation;

    // ZIP code validation
    const zipValidation = validateZipCode(data.zipCode);
    if (zipValidation) errors.zipCode = zipValidation;

    // Foundation Type validation (required)
    const foundationValidation = validateRequired(data.foundationType, 'Foundation Type');
    if (foundationValidation) errors.foundationType = foundationValidation;

    // Property Type validation (required)
    const propertyTypeValidation = validateRequired(data.propertyType, 'Property Type');
    if (propertyTypeValidation) errors.propertyType = propertyTypeValidation;

    // Units validation (required when Property Type is Multi Unit, must be > 1)
    if (data.propertyType === 'multi-unit') {
      const rawUnits = typeof data.units === 'string' ? data.units.trim() : data.units;
      if (rawUnits === '' || rawUnits === null || rawUnits === undefined) {
        errors.units = 'Number of units is required for Multi Unit properties';
      } else {
        const unitsNum = typeof rawUnits === 'number' ? rawUnits : parseInt(String(rawUnits), 10);
        if (isNaN(unitsNum) || unitsNum <= 1) {
          errors.units = 'Number of units must be greater than 1';
        }
      }
    }


    // Contract Expiration Date validation (required)
    if (data.contractDate === undefined || data.contractDate === null) {
      errors.contractDate = 'Contract Expiration Date is required';
    }


    // Number validations
    if (!data.bedrooms || String(data.bedrooms).trim() === '') {
      errors.bedrooms = 'Bedrooms is required';
    } else {
      const bedroomsValidation = validateNumber(data.bedrooms, 'Bedrooms', 1);
      if (bedroomsValidation) errors.bedrooms = bedroomsValidation;
    }

    if (!data.bathrooms || String(data.bathrooms).trim() === '') {
      errors.bathrooms = 'Bathrooms is required';
    } else {
      const bathroomsValidation = validateNumber(data.bathrooms, 'Bathrooms', 1);
      if (bathroomsValidation) errors.bathrooms = bathroomsValidation;
    }

    const priceValidation = validateNumber(data.price.replace(/[$,]/g, ''), 'Price', 0);
    if (priceValidation) errors.price = priceValidation;

    // ARV validation (optional, but must be valid if provided)
    const arvValue = data.arv ? data.arv.replace(/[$,]/g, '') : '';
    if (arvValue) {
      const arvNumberValidation = validateNumber(arvValue, 'After Repair Value', 0);
      if (arvNumberValidation) errors.arv = arvNumberValidation;
    }

    // Image validation (required)
    if (images !== undefined) {
      const imageValidation = validateImages(images);
      if (imageValidation) errors.images = imageValidation;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  return {
    validateRequired,
    validateNumber,
    validateEmail,
    validateZipCode,
    validateImages,
    validatePropertyData,
    checkDuplicateProperty
  };
};
